import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Button: Sprout OS (#358). Pill, semibold, focus ring on --px-focus.
 * Variants (API unchanged from the Salesian LD version):
 *   default:     Primary (Sprout button-primary fill). Page-level primary action.
 *   accent:      Call to action (Sun Gold fill). One per page at most.
 *   destructive: Critical. Delete and other irreversible actions.
 *   outline:     Secondary: white, strong border.
 *   secondary:   Sunken ground, no border.
 *   ghost:       Quiet: transparent, surfaced on hover.
 *   link:        Text link in the link colour.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill border border-transparent text-base font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-button-primary text-button-primary-label hover:underline',
        accent:
          'bg-cta text-cta-label border-cta-edge hover:underline',
        destructive:
          'bg-critical text-white hover:underline',
        outline:
          'border-border-strong bg-surface-raised text-ink-950 hover:bg-surface-sunken',
        secondary:
          'bg-surface-sunken text-ink-950 hover:bg-ink-200',
        ghost:
          'text-ink-950 hover:bg-surface-sunken',
        link:
          'text-link underline-offset-4 hover:underline hover:text-link-hover',
      },
      size: {
        default: 'h-10 px-4 [&_svg]:size-4',
        sm:      'h-8 px-3 text-sm [&_svg]:size-3.5',
        lg:      'h-12 px-6 text-lg [&_svg]:size-5',
        icon:    'h-10 w-10 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
);
Button.displayName = 'Button';

export { buttonVariants };
